import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Home, MessageSquare, Search, MessageCircle, LayoutDashboard,
  BarChart3, Repeat, MapPin, Headphones, HelpCircle, Database,
  Upload, Settings, Menu, ChevronsLeft, LogOut, Moon, Sun, TrendingUp, Layers,
  GitCompareArrows, UserMinus,
} from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';

type NavItem = {
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  to: string;
  /** Extra paths that should also highlight this item as active */
  alsoActiveFor?: string[];
};

type NavSection = {
  id: string;
  items: NavItem[];
};

const SECTIONS: NavSection[] = [
  {
    id: 'main',
    items: [
      { icon: Home, label: 'Home', to: '/' },
    ],
  },
  {
    id: 'analytics',
    items: [
      { icon: MessageSquare, label: 'Ask Claude', to: '/ask-claude' },
      { icon: Search, label: 'Analyze', to: '/analyze' },
      { icon: MessageCircle, label: 'Conversation', to: '/conversation' },
    ],
  },
  {
    id: 'dashboards',
    items: [
      { icon: LayoutDashboard, label: 'Dashboards', to: '/dashboard' },
      {
        icon: BarChart3,
        label: 'Business Performance',
        to: '/dashboard/business-performance',
      },
      { icon: TrendingUp, label: 'Revenue Bridge', to: '/dashboard/revenue-bridge' },
      { icon: Layers, label: 'Pending Bridge', to: '/dashboard/pending-bridge' },
      { icon: UserMinus, label: 'Churn Risk', to: '/dashboard/churn' },
      { icon: Repeat, label: 'Cross-Sell', to: '/crosssell' },
      // V3 pages hidden from nav until the mosquito-rules numbers are socialized;
      // routes stay live at /crosssell-v3 and /crosssell/compare for direct access.
      // { icon: Repeat, label: 'Cross-Sell (New Rules)', to: '/crosssell-v3' },
      // { icon: GitCompareArrows, label: 'Cross-Sell (V3 Check)', to: '/crosssell/compare' },
      { icon: MapPin, label: 'Map', to: '/geo' },
      { icon: Headphones, label: 'WFM', to: '/wfm' },
    ],
  },
  {
    id: 'tools',
    items: [
      { icon: HelpCircle, label: 'Agent Assist', to: '/agent-assist' },
      { icon: Database, label: 'Agent Assist Admin', to: '/agent-assist-admin' },
      { icon: Upload, label: 'My Data', to: '/data' },
    ],
  },
  {
    id: 'admin',
    items: [
      { icon: Settings, label: 'Admin', to: '/admin' },
    ],
  },
];

const STORAGE_KEY = 'nh-sidebar-expanded';

function isActive(pathname: string, item: NavItem): boolean {
  if (pathname === item.to) return true;
  if (item.alsoActiveFor?.some(p => pathname === p)) return true;
  // /dashboard/business-performance highlights itself, not /dashboard
  if (item.to === '/' && pathname === '') return true;
  return false;
}

export default function AppSidebar() {
  const { pathname } = useLocation();
  const { instance, accounts } = useMsal();
  const { theme, toggle: toggleTheme } = useTheme();
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(expanded)); } catch { /* ignore */ }
  }, [expanded]);

  const account = accounts[0];
  const fullName = account?.name || account?.username || 'User';
  const initials = fullName
    .split(/[\s@.]+/)
    .map(s => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'NS';

  const handleLogout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin + '/',
    });
  };

  const width = expanded ? 240 : 56;

  return (
    <aside
      style={{
        width,
        minWidth: width,
        height: '100vh',
        background: 'var(--bg-sidebar)',
        borderRight: '1px solid var(--border-default)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        transition: 'width 140ms ease, min-width 140ms ease',
      }}
    >
      {/* Toggle + logo */}
      <div
        style={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: expanded ? '0 12px' : '0',
          justifyContent: expanded ? 'flex-start' : 'center',
          borderBottom: '1px solid var(--border-subtle)',
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => setExpanded(v => !v)}
          title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          style={{
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            borderRadius: 8,
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          {expanded ? <ChevronsLeft size={18} /> : <Menu size={18} />}
        </button>
        {expanded && (
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            Ned&apos;s Home
          </span>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {SECTIONS.map((section, sectionIdx) => (
          <div key={section.id}>
            {sectionIdx > 0 && (
              <div
                style={{
                  height: 1,
                  background: 'var(--border-subtle)',
                  margin: '6px 12px',
                }}
              />
            )}
            {section.items.map(item => {
              const Icon = item.icon;
              const active = isActive(pathname, item);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  title={expanded ? undefined : item.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: expanded ? '8px 12px' : '8px',
                    margin: '2px 8px',
                    borderRadius: 8,
                    textDecoration: 'none',
                    color: active ? 'var(--ns-primary)' : 'var(--text-secondary)',
                    background: active ? 'var(--accent-bg)' : 'transparent',
                    fontWeight: active ? 600 : 500,
                    fontSize: 13,
                    transition: 'background 120ms, color 120ms',
                    justifyContent: expanded ? 'flex-start' : 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={e => {
                    if (!active) e.currentTarget.style.background = 'var(--bg-hover)';
                  }}
                  onMouseLeave={e => {
                    if (!active) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Icon size={18} />
                  {expanded && <span>{item.label}</span>}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User section */}
      <div
        style={{
          borderTop: '1px solid var(--border-subtle)',
          padding: 8,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: expanded ? '8px 8px' : '4px 0',
            justifyContent: expanded ? 'flex-start' : 'center',
          }}
          title={fullName}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'var(--ns-primary)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
          {expanded && (
            <>
              <span
                style={{
                  flex: 1,
                  fontSize: 13,
                  color: 'var(--text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {fullName}
              </span>
              <button
                onClick={toggleTheme}
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                style={{
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  borderRadius: 6,
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
              </button>
              <button
                onClick={handleLogout}
                title="Sign out"
                style={{
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  borderRadius: 6,
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <LogOut size={14} />
              </button>
            </>
          )}
        </div>
        {!expanded && (
          <>
            <button
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{
                width: '100%',
                height: 32,
                marginTop: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                borderRadius: 6,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            <button
              onClick={handleLogout}
              title="Sign out"
              style={{
                width: '100%',
                height: 32,
                marginTop: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                borderRadius: 6,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-hover)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <LogOut size={14} />
            </button>
          </>
        )}
      </div>
    </aside>
  );
}
